import React from 'react';
import { GlassCard } from '../components/GlassCard';
import { RiskTrendChart } from '../components/charts/RiskTrendChart';
import { MOCK_ALERTS, MOCK_ASSETS } from '../data/mockData';
import { Badge } from '../components/Badge';
import SafeIcon from '../common/SafeIcon';
import { FiShield, FiAlertTriangle, FiServer, FiArrowUpRight, FiArrowDownRight } from 'react-icons/fi';

export const Dashboard = () => {
  const activeAlerts = MOCK_ALERTS.filter(a => a.status === 'active').length;
  const criticalAlerts = MOCK_ALERTS.filter(a => a.severity === 'critical' && a.status === 'active').length;
  
  return (
    <div className="space-y-6 max-w-7xl mx-auto">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-end gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Command Center</h1>
          <p className="text-slate-400 mt-1">Real-time overview of your infrastructure posture.</p>
        </div>
        <div className="flex items-center gap-2 text-sm font-mono text-emerald-400 bg-emerald-500/10 px-3 py-1 rounded-full border border-emerald-500/20">
          <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
          SYSTEMS ONLINE
        </div>
      </div>

      {/* Top Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <GlassCard delay={0.1}>
          <div className="flex justify-between items-start">
            <div>
              <p className="text-slate-400 text-sm font-medium">Global Risk Score</p>
              <h2 className="text-4xl font-bold mt-2 text-red-500">65<span className="text-lg text-slate-500">/100</span></h2>
            </div>
            <div className="p-3 bg-red-500/10 rounded-lg text-red-500">
              <SafeIcon icon={FiShield} className="text-2xl" />
            </div>
          </div>
          <div className="mt-4 flex items-center text-sm text-red-400">
            <SafeIcon icon={FiArrowUpRight} className="mr-1" />
            <span>+12% from last week</span>
          </div>
        </GlassCard>

        <GlassCard delay={0.2}>
          <div className="flex justify-between items-start">
            <div>
              <p className="text-slate-400 text-sm font-medium">Active Alerts</p>
              <h2 className="text-4xl font-bold mt-2 text-orange-400">{activeAlerts}</h2>
            </div>
            <div className="p-3 bg-orange-500/10 rounded-lg text-orange-400">
              <SafeIcon icon={FiAlertTriangle} className="text-2xl" />
            </div>
          </div>
          <div className="mt-4 flex items-center text-sm text-slate-400">
            <span className="text-red-500 font-bold mr-2">{criticalAlerts} Critical</span> requiring attention
          </div>
        </GlassCard>

        <GlassCard delay={0.3}>
          <div className="flex justify-between items-start">
            <div>
              <p className="text-slate-400 text-sm font-medium">Monitored Assets</p>
              <h2 className="text-4xl font-bold mt-2 text-emerald-400">{MOCK_ASSETS.length}</h2>
            </div>
            <div className="p-3 bg-emerald-500/10 rounded-lg text-emerald-400">
              <SafeIcon icon={FiServer} className="text-2xl" />
            </div>
          </div>
          <div className="mt-4 flex items-center text-sm text-emerald-400">
            <SafeIcon icon={FiArrowUpRight} className="mr-1" />
            <span>100% coverage active</span>
          </div>
        </GlassCard>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Chart */}
        <GlassCard delay={0.4} className="lg:col-span-2 h-96 flex flex-col">
          <h3 className="text-lg font-medium mb-4">Risk Posture Trend (7 Days)</h3>
          <div className="flex-1 -ml-4">
            <RiskTrendChart />
          </div>
        </GlassCard>

        {/* Recent Alerts Mini-list */}
        <GlassCard delay={0.5} className="flex flex-col">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-medium">Recent Detections</h3>
            <button className="text-xs text-emerald-400 hover:text-emerald-300">View All</button>
          </div>
          <div className="flex-1 overflow-y-auto pr-2 space-y-4 custom-scrollbar">
            {MOCK_ALERTS.slice(0, 4).map((alert, i) => (
              <div key={i} className="p-3 rounded-lg bg-white/5 border border-white/5 hover:border-white/10 transition-colors">
                <div className="flex justify-between items-start mb-2">
                  <Badge type={alert.severity} />
                  <span className="text-xs text-slate-500">{alert.time}</span>
                </div>
                <p className="text-sm font-medium text-slate-200 truncate">{alert.type}</p>
                <p className="text-xs text-slate-400 font-mono mt-1">{alert.asset}</p>
              </div>
            ))}
          </div>
        </GlassCard>
      </div>
    </div>
  );
};